import { useQuery } from "@apollo/client";
import { useAuth } from "@src/contexts/AuthProvider";
import { gql } from "@src/__generated__";
import { useMemo } from "react";
import {
  useSudoziQuery,
  useSudoziVendorQuery,
} from "@src/infrastructure/useSudoziQuery";
import {
  GetAtlassianConfigurationRoute,
  GetCurrentCompanyRoute,
  GetCurrentPermissionsRoute,
  GetCurrentUserCompanyMembershipsRoute,
  GetIroncladConfigurationRoute,
  GetVendorPortalSessionRoute,
} from "@src/sudozi-schemas/api-route-schemas";

const GET_SESSION_DATA = gql(`
  query GetSessionData {
    applicationConfiguration {
      hasErpClassifications
      submitsSpendRequestsForOthers
      applicationHostname
    }
  }
`);

export function useSession() {
  const { tokenUserData, vendorUser } = useAuth();
  function userHasBeenDecoded() {
    return !tokenUserData?.userId;
  }
  const {
    data: companyData,
    loading: companyLoading,
    error: companyError,
  } = useQuery(GET_SESSION_DATA, {
    skip: !tokenUserData || userHasBeenDecoded() || !!vendorUser,
  });

  const { data: companyMemberships } = useSudoziQuery({
    route: GetCurrentUserCompanyMembershipsRoute,
    queryKey: [GetCurrentUserCompanyMembershipsRoute.path, tokenUserData],
  });
  const { data: currentCompany, refetch: refetchCompany } = useSudoziQuery({
    route: GetCurrentCompanyRoute,
    queryKey: [GetCurrentCompanyRoute.path, tokenUserData, vendorUser],
  });

  const { data: permissions, refetch: refetchPermissions } = useSudoziQuery({
    route: GetCurrentPermissionsRoute,
    queryKey: ["GET /api/current-permissions", tokenUserData, vendorUser],
    options: { enabled: !!tokenUserData && !vendorUser },
  });

  const { data: ironcladConfiguration } = useSudoziQuery({
    route: GetIroncladConfigurationRoute,
    queryKey: ["GET /api/ironclad/configuration", tokenUserData, vendorUser],
  });

  const { data: atlassianConfiguration } = useSudoziQuery({
    route: GetAtlassianConfigurationRoute,
    queryKey: [GetAtlassianConfigurationRoute.path, tokenUserData, vendorUser],
  });

  const { data: vendorCompanyData, refetch: refetchVendorCompanyData } =
    useSudoziVendorQuery({
      route: GetVendorPortalSessionRoute,
      queryKey: [GetVendorPortalSessionRoute.path, tokenUserData, vendorUser],
    });

  const enableAutoCompanyLogos = useMemo(
    () => currentCompany?.enableAutoCompanyLogos ?? false,
    [currentCompany?.enableAutoCompanyLogos],
  );

  const hasErpClassifications = useMemo(
    () => companyData?.applicationConfiguration?.hasErpClassifications ?? false,
    [companyData?.applicationConfiguration?.hasErpClassifications],
  );

  const paymentsEnabled = useMemo(
    () => !!currentCompany?.paymentsEnabled,
    [currentCompany?.paymentsEnabled],
  );

  const paymentsOnboarded = useMemo(
    () => !!currentCompany?.paymentsOnboarded,
    [currentCompany?.paymentsOnboarded],
  );

  const enableIntakeRequests = useMemo(
    () => !!currentCompany?.enableIntakeRequests,
    [currentCompany?.enableIntakeRequests],
  );

  const enableSpendRequests = useMemo(
    () => !!currentCompany?.enableSpendRequests,
    [currentCompany?.enableSpendRequests],
  );

  const enableDocumentSummary = useMemo(
    () => !!currentCompany?.enableDocumentSummary,
    [currentCompany?.enableDocumentSummary],
  );

  const hasSpendRequestAccess = useMemo(
    () => permissions?.hasSpendRequestAccess ?? false,
    [permissions?.hasSpendRequestAccess],
  );

  return {
    currentCompany,
    companyMemberships: companyMemberships ?? [],
    loading: companyLoading,
    error: companyError,
    enableAutoCompanyLogos,
    enableIntakeRequests,
    enableVendorIntake: !!currentCompany?.enableVendorIntake,
    enableN12mServiceSpend: !!currentCompany?.enableN12mServiceSpend,
    enableNetsuitePurchaseOrder: !!currentCompany?.enableNetsuitePurchaseOrder,
    enableEditWithApprovalBypass:
      !!currentCompany?.enableEditWithApprovalBypass,
    enableCurrencies: !!currentCompany?.enableCurrencies,
    enableSourcing: !!currentCompany?.enableSourcing,
    enableBusinessUnits: !!currentCompany?.enableBusinessUnits,
    enableSpendRequests,
    enableDocumentSummary,
    hasErpClassifications,
    paymentsEnabled,
    enablePaymentAccounting: !!currentCompany?.enablePaymentAccounting,
    paymentsOnboarded,
    vendorUser,
    hasSpendRequestAccess,
    isRequestCategoryOwner: permissions?.isRequestCategoryOwner ?? false,
    vendorCompany: vendorCompanyData?.vendorCompany,
    refetchVendorCompanyData,
    hasAdminAccess: permissions?.hasAdminAccess ?? false,
    hasSuperViewerAccess: permissions?.hasSuperViewerAccess ?? false,
    hasDepartmentAccess: permissions?.hasDepartmentAccess ?? false,
    hasDepartmentPayrollAccess:
      permissions?.hasDepartmentPayrollAccess ?? false,
    hasBusinessUnitAccess: permissions?.hasBusinessUnitAccess ?? false,
    hasBusinessUnitPayrollAccess:
      permissions?.hasBusinessUnitPayrollAccess ?? false,
    hasPaymentsAccess: permissions?.hasPaymentsAccess ?? false,
    hasFinancialAnalysisAccess:
      permissions?.hasFinancialAnalysisAccess ?? false,
    hasApprovalBypassAccess: permissions
      ? permissions.hasApprovalBypassAccess || permissions.hasAdminAccess
      : false,
    hasVendorAdminAccess: permissions?.hasVendorAdminAccess ?? false,
    hasUserAdminAccess: permissions?.hasUserAdminAccess ?? false,
    approvesRequestsForDepartment:
      permissions?.approvesRequestsForDepartment ?? false,
    approvesRequestsForBusinessUnit:
      permissions?.approvesRequestsForBusinessUnit ?? false,
    submitsSpendRequestsForOthers:
      permissions?.submitsSpendRequestsForOthers ?? false,
    userId: tokenUserData?.userId ?? vendorUser?.userId ?? "",
    departmentIds: permissions?.departmentIds || [],
    businessUnitIds: permissions?.businessUnitIds || [],
    groupIds: permissions?.groupIds || [],
    locationId: permissions?.locationId ?? null,
    enforceIntakeApprovalOrder:
      currentCompany?.enforceIntakeApprovalOrder ?? false,
    ironcladCompanyId: ironcladConfiguration?.ironcladCompanyId ?? null,
    refetchPermissions,
    refetchCompany,
    applicationHostname:
      companyData?.applicationConfiguration?.applicationHostname ?? "",
    jiraBaseUrl: atlassianConfiguration?.jiraBaseUrl ?? null,
    erpIntegrationType: currentCompany?.erpIntegrationType ?? null,
    enableEntities: !!currentCompany?.enableEntities,
    restrictSubmissionEditToAdmins:
      !!currentCompany?.restrictSubmissionEditToAdmins,
  };
}
